import { useMemo } from "react";

export default function TradeHistoryComponent({ data, target }) {
  const positions = Array.isArray(data?.positions) ? data.positions : [];
  const deals = Array.isArray(data?.deals) ? data.deals : [];
  const fmtNum = (n, f = 2) =>
    Intl.NumberFormat("en-US", { maximumFractionDigits: f }).format(
      Number(n || 0)
    );
  const fmtMoney = (n) =>
    Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  const fmtTime = (s) =>
    s ? new Date(s).toLocaleString("fa-IR", { hour12: false }) : "-";
  const commissionsByPos = useMemo(() => {
    const m = new Map();
    for (const d of deals) {
      const k = d?.position_id ?? 0;
      const c = Number(d?.commission || 0);
      m.set(k, (m.get(k) || 0) + c);
    }
    return m;
  }, [deals]);
  const rows = useMemo(
    () =>
      positions.map((p) => ({
        id: p.position_id,
        deals: p.deal_count ?? 0,
        commission: commissionsByPos.get(p.position_id) || 0,
        profit: p.total_profit ?? 0,
        exit: p.exit_price ?? 0,
        entry: p.entry_price ?? 0,
        volume: p.volume ?? 0,
        type: p.entry_action_type || "-",
        symbol: p.symbol || "-",
        open: p.open_time || null,
      })),
    [positions, commissionsByPos]
  );

  return (
    <div className="w-full items-center justify-center flex flex-col">
      <div className="w-[1000px] flex flex-col items-center justify-center overflow-hidden rounded-md">
        <span className="text-[26px] text-[#FFFFFF] font-bold">
          تاریخچه معاملات
        </span>
        <div className="relative mt-4 mb-[32px] h-5 w-[40rem]">
          <div className="absolute inset-x-20 top-0 h-[3px] w-3/4 bg-gradient-to-r from-transparent via-white/70 to-transparent blur-md" />
          <div className="absolute inset-x-20 top-0 h-px w-3/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_8px_#ffffff]" />
          <div className="absolute inset-x-60 top-0 h-[6px] w-1/4 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-md" />
          <div className="absolute inset-x-60 top-0 h-px w-1/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_10px_#ffffff]" />
          <div className="absolute inset-x-44 top-0 h-[2px] w-2/4 bg-gradient-to-r from-transparent via-sky-100/60 to-transparent blur-[2px]" />
          <div className="pointer-events-none absolute inset-0 h-full w-full [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,white)]" />
        </div>
      </div>

      <div className="history-table-container w-[1074px] flex flex-col mt-[48px] mb-[95px] max-h-[600px] overflow-y-auto">
        <div
          style={{ borderRadius: "29px 29px 0 0" }}
          className="w-full flex items-center justify-center gap-[64px] h-[73px] bg-[#06182D]"
        >
          <span className="text-[16px] text-[#FFFFFF] font-normal">
            معاملات
          </span>
          <span className="text-[16px] text-[#FFFFFF] font-normal">
            کمیسیون
          </span>
          <span className="text-[16px] text-[#FFFFFF] font-normal">سود</span>
          <span className="text-[16px] text-[#FFFFFF] font-normal">
            قیمت بسته
          </span>
          <span className="text-[16px] text-[#FFFFFF] font-normal">
            قیمت باز
          </span>
          <span className="text-[16px] text-[#FFFFFF] font-normal">حجم</span>
          <span className="text-[16px] text-[#FFFFFF] font-normal">نوع</span>
          <span className="text-[16px] text-[#FFFFFF] font-normal">نماد</span>
          <span className="text-[16px] text-[#FFFFFF] font-normal">
            زمان باز کردن
          </span>
        </div>

        {rows.length === 0 && (
          <div
            className="w-full flex items-center justify-center h-[140px] bg-[#0C1A30] text-white"
            style={{ borderRadius: "0 0 29px 29px" }}
          >
            هیچ رکوردی یافت نشد
          </div>
        )}

        {rows.map((r, idx) => {
          const bg = idx % 2 === 0 ? "#1F2F50" : "#06182D";
          const isLast = idx === rows.length - 1;
          const radius = isLast
            ? {
                borderBottomLeftRadius: 29,
                borderBottomRightRadius: 29,
                overflow: "hidden",
              }
            : {};
          return (
            <div
              key={r.id}
              style={{ background: bg, height: 50, ...radius }}
              className="w-full flex items-center justify-center"
            >
              <div className="w-full flex items-center justify-center h-[73px]">
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[154px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {r.deals}
                  </span>
                </div>
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[136.5px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {fmtMoney(r.commission)}
                  </span>
                </div>
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[106px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {fmtMoney(r.profit)}
                  </span>
                </div>
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[115px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {fmtNum(r.exit)}
                  </span>
                </div>
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[132px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {fmtNum(r.entry)}
                  </span>
                </div>
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[99px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {fmtNum(r.volume, 0)}
                  </span>
                </div>
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[82px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {r.type}
                  </span>
                </div>
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[94px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {r.symbol}
                  </span>
                </div>
                <div
                  style={{ borderLeft: "1px solid #06182D" }}
                  className="w-[155px] h-full flex items-center justify-center"
                >
                  <span className="text-[16px] text-white font-normal">
                    {fmtTime(r.open)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
